import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

export type DataSourceMode = 'demo' | 'cloudflare';

const STORAGE_KEY = 'vitalai.data-source-mode';

function readStoredMode(): DataSourceMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'demo' || stored === 'cloudflare') return stored;
  } catch {
    // localStorage puede no estar disponible; usamos el default.
  }
  return 'demo';
}

interface DataSourceModeContextValue {
  mode: DataSourceMode;
  setMode: (mode: DataSourceMode) => void;
}

// Context (no solo un hook con useState local): HealthDataProvider y el
// selector en Perfil necesitan ver y reaccionar al mismo valor de `mode`,
// no dos copias independientes.
const DataSourceModeContext = createContext<DataSourceModeContextValue | null>(null);

export function DataSourceModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<DataSourceMode>(readStoredMode);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Ignorar: la preferencia no persistira entre sesiones.
    }
  }, [mode]);

  const setMode = useCallback((next: DataSourceMode) => setModeState(next), []);

  return <DataSourceModeContext.Provider value={{ mode, setMode }}>{children}</DataSourceModeContext.Provider>;
}

export function useDataSourceMode(): [DataSourceMode, (mode: DataSourceMode) => void] {
  const context = useContext(DataSourceModeContext);
  if (!context) {
    throw new Error('useDataSourceMode debe usarse dentro de <DataSourceModeProvider>');
  }
  return [context.mode, context.setMode];
}
